import { Box } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo } from "react";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import { Button } from "src/components/common/FormComponents/Buttons";
import { Input } from "src/components/common/FormComponents/Input";
import errorMessage from "src/constants/errorMessage";
import {
  getFundApplicationById,
  getFundRequest,
  getLandDetailsById,
} from "src/utils/api/createProjectFlow";
import defaultStyles from "./styles";
import { fillFundApplicationAPI } from "src/utils/api/dashboard";
import { getFinancialYear } from "src/utils/helper";
import {
  ActionFooter,
  DetailField,
  DetailFieldGrid,
  hasPreviousUcDetails,
  PreviousUcSummary,
  SectionCard,
  previousUcFromFaDetail,
  fundUi,
  money,
  ordinal,
} from "../ProjectDetailView/FundApplicationDetails/FundApplicationUI";

export type demandNoteFormData = {
  demandRecipt: {
    type: string;
    key: string;
    file: string;
  }[];
};

/** Vertical rhythm between the money inputs — the error text sits below each. */
const fieldStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: "26px",
};

const RaiseFundApplication = () => {
  const [searchParams] = useSearchParams();

  const EPid = searchParams.get("id") || searchParams.get("landOwnershipEpid");

  const navigate = useNavigate();

  // The FA is being raised today, so its financial year is simply the
  // current Indian financial year — no need to ask the user to pick one.
  // Displayed/stored as "YYYY-YY" (e.g. "2026-27") to match this field's
  // existing short-year convention, so re-derive that from the canonical
  // (long-format) getFinancialYear rule shared with the admin portal.
  const currentFinancialYear = getFinancialYear(new Date()).replace(
    /-(\d{2})(\d{2})$/,
    "-$2"
  );

  const { isLoading, mutate } = useMutation(
    "fillFundApllicationForm",
    ({ EPid, data }: { EPid: string; data: any }) =>
      fillFundApplicationAPI(EPid, data),
    {
      onSuccess: () => {
        navigate("/");
      },
      onError: (error: any) => {},
    }
  );

  const { data: userData } = useQuery(
    ["userDetails", EPid],
    () => getLandDetailsById(EPid),
    {
      onSuccess: (res: AxiosResponse) => {},
      onError: (req: AxiosError) => {},
      enabled: !!EPid,
      refetchOnMount: true,
    }
  );

  const { data: existingFAs } = useQuery(
    ["existingFAs", EPid],
    () =>
      getFundRequest({
        limit: 100,
        offset: 0,
        sortBy: "yearOfFundApplication:DESC",
      }),
    { enabled: !!EPid }
  );

  // The latest FA already raised against this EPID — the one whose utilization
  // certificate this new application follows on from.
  const previousFa = useMemo(() => {
    const rows: any[] = existingFAs?.data?.rows ?? [];
    const forEpid = rows.filter(
      (row: any) => row.landOwnershipEpid === EPid && row.yearOfFundApplication
    );

    return forEpid.reduce(
      (latest: any, row: any) =>
        (row.yearOfFundApplication ?? 0) > (latest?.yearOfFundApplication ?? 0)
          ? row
          : latest,
      null
    );
  }, [existingFAs, EPid]);

  // Fetched by id because the list rows carry document *keys*, not the signed
  // URLs the certificate tiles need.
  const { data: previousFaDetail } = useQuery(
    ["previousFaDetail", previousFa?.id],
    () => getFundApplicationById(previousFa?.id),
    { enabled: !!previousFa?.id }
  );

  const previousUc = previousUcFromFaDetail(previousFaDetail?.data);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<any>({
    mode: "onChange",
    defaultValues: {
      // The Estimated Amount inputs were removed from this form (SNOs were
      // filling in the same five figures twice, once here and once under
      // Amount Required) but the backend's CreateFundRequestDto still
      // requires these keys on submit, so they stay fixed at 0 rather than
      // being user-entered.
      estimatedAdvanceWorkCost: 0,
      estimatedMonitoringCost: 0,
      estimatedRaisingCost: 0,
      estimatedMaintenanceCost: 0,
      estimatedContingencyCost: 0,
      totalEstimatedCost: 0,
      advanceWorkCost: "",
      raisingCost: "",
      maintainceCost: "",
      monitoringCost: "",
      contingencyCost: "",
      totalCost: "",
      additionalInfo: "",
      yearOfFundApplication: "",
      financialYear: currentFinancialYear,
      // utilizationCertificateDocId: "",
    },
  });

  useEffect(() => {
    if (!existingFAs?.data?.rows || !EPid) return;
    const fasForEpid = existingFAs.data.rows.filter(
      (row: any) => row.landOwnershipEpid === EPid
    );
    const maxYear = fasForEpid.reduce(
      (max: number, row: any) => Math.max(max, row.yearOfFundApplication || 0),
      0
    );
    const nextYear = maxYear + 1;
    if (nextYear >= 1 && nextYear <= 10) {
      setValue("yearOfFundApplication", nextYear);
    }
  }, [existingFAs, EPid, setValue]);

  const handleForm = (data: any) => {
    const excludeFields = [
      "additionalInfo",
      "financialYear",
      // "utilizationCertificateDocId",
    ];

    const numericData = Object.fromEntries(
      Object.entries(data).map(([key, value]) =>
        excludeFields.includes(key) ? [key, value] : [key, Number(value)]
      )
    );
    mutate({
      EPid,
      data: numericData,
    });
  };

  const advanceWorkCost = watch("advanceWorkCost");
  const raisingCost = watch("raisingCost");
  const maintenanceCost = watch("maintenanceCost");
  const monitoringCost = watch("monitoringCost");
  const contingencyCost = watch("contingencyCost");

  useEffect(() => {
    const totalCost =
      (parseFloat(advanceWorkCost) || 0) +
      (parseFloat(raisingCost) || 0) +
      (parseFloat(maintenanceCost) || 0) +
      (parseFloat(monitoringCost) || 0) +
      (parseFloat(contingencyCost) || 0);

    setValue("totalCost", totalCost.toFixed(2));
  }, [
    advanceWorkCost,
    raisingCost,
    maintenanceCost,
    monitoringCost,
    contingencyCost,
    setValue,
  ]);

  const land = userData?.data;

  const totalArea = Number(land?.totalArea);

  /** One money input, styled like the detail page's fields. */
  const amountField = (
    name: string,
    label: string,
    options?: { readOnly?: boolean }
  ) => (
    <Input
      name={name}
      label={label}
      placeholder={options?.readOnly ? "Calculated automatically" : label}
      control={control}
      errors={errors}
      rules={{ required: errorMessage.required }}
      readOnly={options?.readOnly}
      type="number"
      customStyles={{
        label: { fontSize: "13px" },
        input: options?.readOnly
          ? {
              backgroundColor: fundUi.softGreen,
              color: fundUi.deepGreen,
              fontWeight: 600,
            }
          : {},
      }}
    />
  );

  return (
    <Box component="form" onSubmit={handleSubmit(handleForm)}>
      <DashboardWrapper
        title="Fund Application Form"
        customStyles={{
          children: {
            marginTop: "20px",
            padding: { xs: "16px", md: "22px" },
            paddingBottom: "40px",
            borderRadius: "18px",
            backgroundColor: "#F5FAF8",
          },
        }}
        showBackBtn
      >
        <Box>
          <Box
            sx={{
              fontSize: "22px",
              fontWeight: 600,
              color: fundUi.value,
            }}
          >
            {`${ordinal(
              Number(watch("yearOfFundApplication")) || 1
            )} Fund Application`}
          </Box>
          <Box
            sx={{
              fontSize: "14px",
              color: fundUi.label,
              marginTop: "6px",
            }}
          >
            {`Financial Year ${currentFinancialYear}`}
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "repeat(2, minmax(0, 1fr))",
            },
            gap: "20px",
            alignItems: "stretch",
            marginTop: "20px",
          }}
        >
          <SectionCard
            title="Eco-Restoration Blocks Details"
            icon={<ParkOutlinedIcon />}
            accent
            fullWidth
          >
            <DetailFieldGrid columns={3}>
              <DetailField
                label="Entity Name"
                value={land?.user?.firstName}
                span
              />
              <DetailField
                label="Project Registration No."
                value={land?.projectDetail?.registrationNo}
              />
              <DetailField label="Cost Estimate ID" value={land?.id} />
              <DetailField
                label="Eco-Restoration Cost"
                value={land?.demandNote ? money(land?.demandNote) : undefined}
              />
              <DetailField
                label="State"
                value={land?.projectDetail?.address?.stateDetail?.name}
              />
              <DetailField
                label="District"
                value={land?.projectDetail?.address?.districtDetail?.name}
              />
              <DetailField
                label="Total Land Area (Hectares)"
                value={
                  Number.isFinite(totalArea) ? totalArea.toFixed(2) : undefined
                }
              />
              <DetailField
                label="Division"
                value={land?.projectDetail?.landSpecification?.division}
              />
              <DetailField
                label="Range"
                value={land?.projectDetail?.landSpecification?.range}
              />
            </DetailFieldGrid>
          </SectionCard>

          {hasPreviousUcDetails(previousUc) && (
            <SectionCard
              title="Previous UC"
              icon={<DescriptionOutlinedIcon />}
              fullWidth
            >
              <PreviousUcSummary uc={previousUc} />
            </SectionCard>
          )}

          <SectionCard
            title="Amount Required (In Rupees)"
            icon={<CurrencyRupeeIcon />}
          >
            <Box sx={fieldStackSx}>
              {amountField("advanceWorkCost", "Advance Work Cost")}
              {amountField("raisingCost", "Raising/Creation Cost (If Any)")}
              {amountField("maintenanceCost", "Maintance Cost (If Any)")}
              {amountField("monitoringCost", "Mointoring Cost (If Any)")}
              {amountField("contingencyCost", "Contingency Cost (If Any)")}
              {amountField("totalCost", "Total Cost", { readOnly: true })}
            </Box>
          </SectionCard>

          <SectionCard
            title="Additional Information"
            icon={<InfoOutlinedIcon />}
          >
            <Input
              required
              name="additionalInfo"
              label="Additional Information"
              placeholder="(In 500 characters)"
              control={control}
              errors={errors}
              rules={{
                required: errorMessage.required,
                maxLength: {
                  value: 500,
                  message: "More then 500 characters are not allowed",
                },
              }}
              type="text"
              multiline
              minRows={6}
              inputProps={{ style: { overflowY: "auto", resize: "none" } }}
              customStyles={{
                label: { fontSize: "13px" },
                // Fills the gap left below it now that this card sits beside
                // Amount Required instead of spanning the full row - each
                // level (wrapper, then the input itself in multilineInput)
                // has to carry height:100% down the chain for it to reach
                // the textarea.
                wrapper: {
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                },
                input: defaultStyles.multilineInput,
              }}
            />
          </SectionCard>
        </Box>

        <ActionFooter>
          <Button
            text="Submit"
            type="submit"
            loading={isLoading}
            disabled={!isValid || isLoading}
            customStyles={{
              root: {
                fontSize: "16px",
              },
            }}
          />
        </ActionFooter>
      </DashboardWrapper>
    </Box>
  );
};

export default RaiseFundApplication;
